"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Pin,
  Trash2,
  Tag,
  StickyNote,
  Loader2,
} from "lucide-react";
import { useStore } from "@/lib/store";
import type { Note } from "@/lib/types";
import { CommentsSection } from "@/components/comments/CommentsSection";
import { Markdown } from "@/components/ui/Markdown";
import { Avatar } from "@/components/ui/Avatar";
import { BotBadge } from "@/components/ui/BotBadge";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/timezone";

export function NotesView({ canWrite }: { canWrite: boolean }) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const allNotes = useStore((s) => s.notes);
  const selectedNoteId = useStore((s) => s.selectedNoteId);
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const users = useStore((s) => s.users);
  const createNote = useStore((s) => s.createNote);
  const updateNote = useStore((s) => s.updateNote);
  const deleteNote = useStore((s) => s.deleteNote);

  const notes = useMemo(
    () => allNotes.filter((n) => n.workspaceId === activeWorkspaceId),
    [allNotes, activeWorkspaceId]
  );

  const [composerOpen, setComposerOpen] = useState(false);
  const [composerBody, setComposerBody] = useState("");
  const [composerTags, setComposerTags] = useState("");
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const selectedNoteAvailable = notes.some((note) => note.id === selectedNoteId);

  useEffect(() => {
    if (!selectedNoteId || !selectedNoteAvailable) return;
    setTagFilter(null);
    const frame = requestAnimationFrame(() => {
      selectedRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
      selectedRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeWorkspaceId, selectedNoteId, selectedNoteAvailable]);

  // Aggregate all tags for filter chips
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const n of notes) for (const t of n.tags) set.add(t);
    return Array.from(set).sort();
  }, [notes]);

  const visible = useMemo(() => {
    const filtered = tagFilter ? notes.filter((n) => n.tags.includes(tagFilter)) : notes;
    return [...filtered].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return b.createdAt - a.createdAt;
    });
  }, [notes, tagFilter]);

  function submit() {
    if (!canWrite) return;
    if (!composerBody.trim()) return;
    const tags = composerTags
      .split(/[,\s]+/)
      .map((t) => t.replace(/^#/, "").trim())
      .filter(Boolean);
    createNote({ body: composerBody.trim(), tags });
    setComposerBody("");
    setComposerTags("");
    setComposerOpen(false);
  }

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="max-w-3xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-[18px] font-semibold">Notes</h2>
            <p className="text-[12.5px] text-[var(--fg-tertiary)] mt-0.5">
              Quick captures. Agents write here via <code>create_note</code>; humans via the
              composer below.
            </p>
          </div>
          {canWrite && (
            <button
              onClick={() => setComposerOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[12.5px] font-medium transition-colors"
            >
              <Plus size={13} />
              New note
            </button>
          )}
        </div>

        <AnimatePresence>
          {canWrite && composerOpen && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="rounded-md border border-[var(--border-strong)] bg-[var(--bg-secondary)] p-3 mb-4 space-y-2"
            >
              <textarea
                autoFocus
                value={composerBody}
                onChange={(e) => setComposerBody(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder="What do you want to remember? Markdown supported."
                className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-2 text-[13px] outline-none focus:border-[var(--accent)] resize-none min-h-[90px] font-mono"
                maxLength={16384}
              />
              <div className="flex items-center gap-2">
                <Tag size={12} className="text-[var(--fg-tertiary)]" />
                <input
                  value={composerTags}
                  onChange={(e) => setComposerTags(e.target.value)}
                  placeholder="Tags (space or comma separated)"
                  className="flex-1 bg-transparent border-b border-[var(--border)] text-[11.5px] outline-none focus:border-[var(--fg-tertiary)] py-1"
                />
                <div className="text-[10.5px] text-[var(--fg-muted)]">
                  <kbd className="kbd">⌘</kbd> <kbd className="kbd">Enter</kbd>
                </div>
                <button
                  onClick={() => {
                    setComposerOpen(false);
                    setComposerBody("");
                    setComposerTags("");
                  }}
                  className="px-2 py-1 text-[11.5px] text-[var(--fg-tertiary)] hover:text-[var(--fg-secondary)]"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={!composerBody.trim()}
                  className="px-2.5 py-1 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-40 text-white text-[11.5px] font-medium"
                >
                  Save
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {allTags.length > 0 && (
          <div className="flex items-center gap-1.5 mb-4 flex-wrap">
            <span className="text-[10.5px] uppercase tracking-wide text-[var(--fg-muted)] font-medium mr-1">
              Tags
            </span>
            <button
              onClick={() => setTagFilter(null)}
              className={cn(
                "px-2 py-0.5 rounded-full text-[10.5px] border transition-colors",
                tagFilter === null
                  ? "bg-[var(--accent-bg)] border-[var(--accent)] text-[var(--accent)]"
                  : "border-[var(--border)] text-[var(--fg-tertiary)] hover:border-[var(--border-strong)]"
              )}
            >
              All
            </button>
            {allTags.map((t) => (
              <button
                key={t}
                onClick={() => setTagFilter(tagFilter === t ? null : t)}
                className={cn(
                  "px-2 py-0.5 rounded-full text-[10.5px] border transition-colors",
                  tagFilter === t
                    ? "bg-[var(--accent-bg)] border-[var(--accent)] text-[var(--accent)]"
                    : "border-[var(--border)] text-[var(--fg-tertiary)] hover:border-[var(--border-strong)]"
                )}
              >
                #{t}
              </button>
            ))}
          </div>
        )}

        {visible.length === 0 ? (
          <div className="text-center py-16">
            <StickyNote size={28} className="mx-auto text-[var(--fg-muted)] mb-3" />
            <p className="text-[13px] text-[var(--fg-tertiary)]">
              {tagFilter
                ? `No notes tagged #${tagFilter}.`
                : "No notes yet. Capture anything worth remembering — agents and humans share this feed."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map((n, i) => (
              <div key={n.id} ref={n.id === selectedNoteId ? selectedRef : undefined}
                tabIndex={n.id === selectedNoteId ? -1 : undefined}
                className={n.id === selectedNoteId ? "rounded-md ring-2 ring-[var(--accent)] scroll-mt-4 focus:outline-none" : undefined}
                aria-label={n.id === selectedNoteId ? "Nota vinculada" : undefined}>
              <NoteRow
                note={n}
                delay={i * 0.02}
                canWrite={canWrite}
                author={users.find((u) => u.id === n.authorId)}
                onTogglePin={() => {
                  if (!canWrite) return;
                  updateNote(n.id, { pinned: !n.pinned });
                }}
                onDelete={() => {
                  if (!canWrite) return;
                  if (confirm("Delete this note? (soft — recoverable from trash)"))
                    deleteNote(n.id);
                }}
              />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NoteRow({
  note,
  delay,
  canWrite,
  author,
  onTogglePin,
  onDelete,
}: {
  note: Note;
  delay: number;
  canWrite: boolean;
  author?: { id: string; name: string; email: string; initials: string; color: string };
  onTogglePin: () => void;
  onDelete: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-3 hover:border-[var(--border-strong)] transition-colors group"
    >
      <div className="flex items-start gap-3">
        <Avatar user={author} size={22} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--fg-tertiary)] mb-1.5 flex-wrap">
            <span className="font-medium text-[var(--fg)]">{author?.name ?? "Unknown"}</span>
            <BotBadge authorId={note.authorId} />
            <span className="text-[var(--fg-muted)]">·</span>
            <span>{formatRelativeTime(note.createdAt)}</span>
            {note.tags.length > 0 && (
              <>
                <span className="text-[var(--fg-muted)]">·</span>
                <div className="flex items-center gap-1 flex-wrap">
                  {note.tags.map((t) => (
                    <span
                      key={t}
                      className="px-1.5 py-0.5 rounded-full text-[10px] border border-[var(--border)] text-[var(--fg-tertiary)]"
                    >
                      #{t}
                    </span>
                  ))}
                </div>
              </>
            )}
            {note.pinned && (
              <>
                <span className="text-[var(--fg-muted)]">·</span>
                <Pin size={10} className="text-[var(--accent)]" />
              </>
            )}
          </div>
          <div className="text-[13px] text-[var(--fg-secondary)]">
            <Markdown content={note.body} />
          </div>
          <CommentsSection
            parentType="note"
            parentId={note.id}
            comments={note.comments ?? []}
            defaultOpen={(note.comments ?? []).length > 0}
            canWrite={canWrite}
          />
        </div>
        {canWrite && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <button
              onClick={onTogglePin}
              className={cn(
                "w-6 h-6 rounded flex items-center justify-center hover:bg-[var(--bg-hover)] transition-colors",
                note.pinned ? "text-[var(--accent)]" : "text-[var(--fg-tertiary)]"
              )}
              title={note.pinned ? "Unpin" : "Pin"}
            >
              <Pin size={11} />
            </button>
            <button
              onClick={onDelete}
              className="w-6 h-6 rounded flex items-center justify-center text-[var(--fg-tertiary)] hover:text-[var(--red)] hover:bg-[var(--bg-hover)] transition-colors"
              title="Delete"
            >
              <Trash2 size={11} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
